import { FC } from 'react';
import {
  Block as BlockIcon,
  RateReview,
  CancelScheduleSend,
  ThumbDown,
  HourglassEmpty,
  ThumbUp,
  Event,
  MergeType,
  Done,
  CallMissedOutgoing,
  AccessTimeOutlined,
  HourglassFullOutlined,
  LoopOutlined,
  ReportProblemOutlined,
  ErrorOutline,
  QuestionMark,
} from '@mui/icons-material';
import BuildingIcon from 'packages/lib/assets/icons/loading-icon.svg?react';
import { TPStatus } from 'packages/main/components/training-platform/api';
import {
  blue,
  green,
  grey,
  indigo,
  lightBlue,
  lightGreen,
  orange,
  purple,
  red,
  yellow,
} from '@mui/material/colors';
import { makeStyles } from 'tss-react/mui';
import {
  AnnotationState,
  TaskGroupState,
  TaskState,
  annotationStateZod,
  taskGroupStateZod,
  taskStateZod,
} from 'packages/lib/api/annotations/contract/enums';
import { FlagOutlined, QuestionOutlined } from '@ant-design/icons';
import React from 'react';

// Allow MUI icons, Antd icons, and SVG icons
type IconType = typeof Event | typeof FlagOutlined | typeof BuildingIcon;

export enum GenericState {
  Success = 'GSSuccess',
  Warning = 'GSWarning',
  Error = 'GSError',
}

export enum JobMetricsEventType {
  Start = 'START',
  Complete = 'COMPLETE',
  Fail = 'FAIL',
}

export enum LinterStatus {
  Succeeded = 'succeeded',
  Failed = 'failed',
}

// Used by Data Discovery to show status of items sent to be annotated
export enum JobStatusType {
  // The following are meant to be displayed on top of a white or bright background:
  Succeeded = 'succeeded',
  Failed = 'failed',
  Pending = 'pending',
  Received = 'received',
  Autolabeling = 'autolabeling',
  GeneratingPose = 'generatingpose',
  Queued = 'queued',
  Processing = 'processing',
  Unavailable = 'unavailable', // Can't annotate this GO, since a GO from this timestamp (but different cluster_id) was already sent to be annotated
  // The following are meant to be displayed on top of a dark background:
  SucceededBright = 'succeededbright',
  FailedBright = 'failedbright',
  PendingBright = 'pendingbright',
  QueuedBright = 'queuedbright',
  ReceivedBright = 'receivedbright',
  AutolabelingBright = 'autolabelingbright',
  GeneratingPoseBright = 'generatingposebright',
  ProcessingBright = 'processingbright',
  UnavailableBright = 'unavailablebright',
  ErrorBadRequest = 'error: BadRequest',
  ErrorBadGateway = 'error: BadGateway',
  Error = 'error',
  NotFound = 'Not Found',
}

type StateIconType =
  | TaskGroupState
  | TaskState
  | TPStatus
  | GenericState
  | LinterStatus
  | JobStatusType
  | JobMetricsEventType
  | AnnotationState
  | 'Redo';

const stateIconMap: Record<StateIconType, IconType> = {
  [taskGroupStateZod.enum.ToDo]: Event,
  [taskGroupStateZod.enum.InProgress]: HourglassEmpty,
  [taskGroupStateZod.enum.InReview]: RateReview,
  [taskGroupStateZod.enum.Accepted]: ThumbUp,
  [taskStateZod.enum.Done]: Done,
  [taskGroupStateZod.enum.Cancelled]: CancelScheduleSend,
  [taskGroupStateZod.enum.Rejected]: ThumbDown,
  [taskStateZod.enum.Merged]: MergeType,
  Redo: CallMissedOutgoing,

  [annotationStateZod.enum.AnnotationInProgress]: HourglassEmpty,
  [annotationStateZod.enum.AnnotationInReview]: RateReview,
  [annotationStateZod.enum.QaToDo]: Event,
  [annotationStateZod.enum.QaInProgress]: HourglassEmpty,
  [annotationStateZod.enum.QaDone]: ThumbUp,
  [annotationStateZod.enum.AuditToDo]: Event,
  [annotationStateZod.enum.AuditInProgress]: RateReview,
  [annotationStateZod.enum.AuditDone]: ThumbUp,
  [annotationStateZod.enum.Reopened]: AccessTimeOutlined,
  [annotationStateZod.enum.Flagged]: FlagOutlined,
  [annotationStateZod.enum.FlaggedInReview]: FlagOutlined,
  [annotationStateZod.enum.FlaggedDone]: ThumbUp,
  [annotationStateZod.enum.Error]: ErrorOutline,
  [annotationStateZod.enum.ErrorInReview]: ErrorOutline,

  [TPStatus.Building]: BuildingIcon,
  [TPStatus.Pending]: AccessTimeOutlined,
  [TPStatus.Aborting]: ErrorOutline,
  [TPStatus.Aborted]: ErrorOutline,
  [TPStatus.Running]: HourglassEmpty,
  [TPStatus.Restarting]: LoopOutlined,
  [TPStatus.Completing]: HourglassFullOutlined,
  [TPStatus.Completed]: Done,
  [TPStatus.Terminating]: ErrorOutline,
  [TPStatus.Terminated]: ErrorOutline,
  [TPStatus.Failed]: ReportProblemOutlined,
  [TPStatus.Unknown]: QuestionOutlined,

  [GenericState.Success]: Done,
  [GenericState.Warning]: ErrorOutline,
  [GenericState.Error]: ReportProblemOutlined,

  [LinterStatus.Succeeded]: Done,
  [LinterStatus.Failed]: ReportProblemOutlined,

  [JobStatusType.Pending]: HourglassEmpty,
  [JobStatusType.Processing]: HourglassEmpty,
  [JobStatusType.Queued]: HourglassEmpty,
  [JobStatusType.Unavailable]: BlockIcon,
  [JobStatusType.Received]: HourglassEmpty,
  [JobStatusType.Autolabeling]: HourglassEmpty,
  [JobStatusType.GeneratingPose]: HourglassEmpty,

  [JobStatusType.PendingBright]: HourglassEmpty,
  [JobStatusType.ProcessingBright]: HourglassEmpty,
  [JobStatusType.QueuedBright]: HourglassEmpty,
  [JobStatusType.SucceededBright]: Done,
  [JobStatusType.FailedBright]: ReportProblemOutlined,
  [JobStatusType.UnavailableBright]: BlockIcon,
  [JobStatusType.ReceivedBright]: HourglassEmpty,
  [JobStatusType.AutolabelingBright]: HourglassEmpty,
  [JobStatusType.GeneratingPoseBright]: HourglassEmpty,
  [JobStatusType.ErrorBadRequest]: ErrorOutline,
  [JobStatusType.ErrorBadGateway]: ErrorOutline,
  [JobStatusType.Error]: ErrorOutline,
  [JobStatusType.NotFound]: QuestionOutlined,

  [JobMetricsEventType.Start]: HourglassEmpty,
  [JobMetricsEventType.Fail]: ReportProblemOutlined,
  [JobMetricsEventType.Complete]: Done,
};

// Figma: https://www.figma.com/file/M8d6EJGihC3bYPEgr6EoTg/Status-Exploration?node-id=108%3A434

const StatusColor = {
  Blue: blue[800],
  Green: green[800],
  Red: red[800],
  Orange: '#F9A725',
};

const colorMap: Record<StateIconType, string> = {
  [taskGroupStateZod.enum.ToDo]: indigo[800],
  [taskGroupStateZod.enum.InProgress]: StatusColor.Blue,
  [taskGroupStateZod.enum.InReview]: yellow[800],
  [taskGroupStateZod.enum.Accepted]: StatusColor.Green,
  [taskStateZod.enum.Done]: StatusColor.Green,
  [taskGroupStateZod.enum.Cancelled]: grey[800],
  [taskGroupStateZod.enum.Rejected]: StatusColor.Red,
  [taskStateZod.enum.Merged]: red[100],
  Redo: lightBlue[800],

  [annotationStateZod.enum.AnnotationInProgress]: StatusColor.Blue,
  [annotationStateZod.enum.AnnotationInReview]: yellow[800],
  [annotationStateZod.enum.QaToDo]: indigo[800],
  [annotationStateZod.enum.QaInProgress]: StatusColor.Blue,
  [annotationStateZod.enum.QaDone]: StatusColor.Green,
  [annotationStateZod.enum.AuditToDo]: indigo[800],
  [annotationStateZod.enum.AuditInProgress]: StatusColor.Blue,
  [annotationStateZod.enum.AuditDone]: StatusColor.Green,
  [annotationStateZod.enum.Reopened]: grey[800],
  [annotationStateZod.enum.Flagged]: yellow[800],

  [annotationStateZod.enum.FlaggedInReview]: StatusColor.Blue,
  [annotationStateZod.enum.FlaggedDone]: StatusColor.Green,
  [annotationStateZod.enum.Error]: StatusColor.Red,
  [annotationStateZod.enum.ErrorInReview]: StatusColor.Blue,

  [TPStatus.Building]: StatusColor.Blue,
  [TPStatus.Pending]: StatusColor.Blue,
  [TPStatus.Running]: StatusColor.Blue,
  [TPStatus.Restarting]: StatusColor.Blue,
  [TPStatus.Completing]: StatusColor.Green,
  [TPStatus.Completed]: StatusColor.Green,
  [TPStatus.Terminating]: StatusColor.Orange,
  [TPStatus.Terminated]: StatusColor.Orange,
  [TPStatus.Aborting]: StatusColor.Orange,
  [TPStatus.Aborted]: StatusColor.Orange,
  [TPStatus.Failed]: StatusColor.Red,
  [TPStatus.Unknown]: 'black',

  [GenericState.Success]: StatusColor.Green,
  [GenericState.Warning]: StatusColor.Orange,
  [GenericState.Error]: StatusColor.Red,

  [LinterStatus.Succeeded]: StatusColor.Green,
  [LinterStatus.Failed]: StatusColor.Red,
  [JobStatusType.Pending]: StatusColor.Blue,
  [JobStatusType.Processing]: purple[800],
  [JobStatusType.Queued]: StatusColor.Blue,
  [JobStatusType.Unavailable]: StatusColor.Orange,
  [JobStatusType.Received]: StatusColor.Blue,
  [JobStatusType.Autolabeling]: purple[800],
  [JobStatusType.GeneratingPose]: purple[800],

  [JobStatusType.ProcessingBright]: lightBlue[300],
  [JobStatusType.PendingBright]: lightBlue[300],
  [JobStatusType.QueuedBright]: purple[300],
  [JobStatusType.SucceededBright]: lightGreen[300],
  [JobStatusType.FailedBright]: red[300],
  [JobStatusType.UnavailableBright]: orange[300],
  [JobStatusType.ReceivedBright]: lightBlue[300],
  [JobStatusType.AutolabelingBright]: lightBlue[300],
  [JobStatusType.GeneratingPoseBright]: lightBlue[300],
  [JobStatusType.ErrorBadRequest]: red[500],
  [JobStatusType.ErrorBadGateway]: red[500],
  [JobStatusType.Error]: red[500],
  [JobStatusType.NotFound]: red[500],

  [JobMetricsEventType.Start]: StatusColor.Blue,
  [JobMetricsEventType.Fail]: StatusColor.Red,
  [JobMetricsEventType.Complete]: StatusColor.Green,
};

export interface StateIconProps {
  state: StateIconType;
  opaque?: boolean;
  missingStateIcon?: IconType;
  style?: React.CSSProperties;
}

const useStateIconStyles = makeStyles()(() => ({
  root: {
    marginRight: '0.25rem',
    verticalAlign: 'text-bottom',
  },
}));
export const StateIcon: FC<React.PropsWithChildren<StateIconProps>> = ({
  state,
  opaque = false,
  missingStateIcon = QuestionMark,
  ...props
}) => {
  const { style, ...restProps } = props;
  const Icon = stateIconMap[state] ?? missingStateIcon;
  const color = colorMap[state] ?? 'orange';
  const { classes } = useStateIconStyles();
  if (!Icon) return null;

  return (
    <Icon
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      className={classes.root}
      {...restProps}
      style={{
        ...style,
        backgroundColor: opaque ? 'white' : 'transparent',
        color,
      }}
    />
  );
};
export default StateIcon;
